<template>
  <footer class="fast-footer fast-ecommerce-footer">
    <!-- Footer Main Section -->
    <div class="fast-footer-main tw-w-full tw-px-4 md:tw-px-[100px]">
      <div
        class="fast-footer-widgets tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tw-gap-y-6 lg:tw-gap-y-0 lg:tw-gap-x-20 xl:tw-gap-x-[210px]"
      >
        <!-- About Section -->
        <FooterAbout />

        <!-- Widget Links -->
        <div class="lg:tw-col-start-2">
          <FooterWidgetLink />
        </div>

        <!-- Contact Section -->
        <FooterContact />
      </div>
    </div>

    <div class="fast-footer-social-mobile tw-flex tw-justify-center md:tw-hidden">
      <FooterSocialContact icon-size="22px" />
    </div>

    <!-- Footer Bottom Section -->
    <div class="fast-footer-copyright tw-w-full tw-px-4 md:tw-px-[100px]">
      <div class="fast-footer-copyright-inner tw-w-full tw-pt-6">
        <div class="tw-flex tw-flex-col tw-items-center tw-gap-4 md:tw-flex-row md:tw-justify-between">
          <!-- Copyright Text -->
          <div class="tw-text-center md:tw-text-left tw-order-1">
            <span class="fast-footer-copyright-text">
              © 2020 {{ websiteName }}. All Rights Reserved. Powered by
              <a
                :href="platformUrl"
                :title="platformName"
                target="_blank"
                class="hover:tw-text-primary-dark"
              >
                {{ platformName }}
              </a>
            </span>
          </div>

          <!-- Social Contact for Desktop -->
          <div class="tw-order-2 tw-hidden md:tw-block">
            <FooterSocialContact icon-size="22px" />
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
const websiteInfoStore = useWebsiteInfoStore()
const websiteName = computed(() => websiteInfoStore?.name || 'Your Website')
const { platformUrl, platformName } = useBrand()
</script>

<style scoped>
.fast-ecommerce-footer {
  @apply tw-flex tw-flex-col tw-gap-10 tw-pt-10 tw-pb-6;
}

.fast-footer-widgets {
  @apply tw-items-start;
}

.fast-footer-copyright-text {
  font-size: 14px;
  line-height: 20px;
}

.fast-footer-copyright-inner {
  border-top: 0.5px solid currentColor;
}

:deep(.fast-footer-title) {
  margin-bottom: 24px;
  font-size: 20px;
  line-height: 30px;
  font-weight: 500;
}

:deep(.web-description),
:deep(.web-description li),
:deep(.fast-footer-widget li),
:deep(address),
:deep(address a),
:deep(address p),
:deep(address div),
:deep(.fast-footer-copyright a) {
  font-size: 14px;
  line-height: 20px;
}

:deep(.tw-footer-logo) {
  height: 40px;
}

:deep(.web-description) {
  row-gap: 12px;
}

:deep(.fast-footer-widget) {
  row-gap: 24px;
}

:deep(.fast-footer-widget > ul) {
  margin-top: 0 !important;
  column-gap: 56px;
  row-gap: 12px;
}

:deep(address) {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(address > div) {
  margin-top: 0 !important;
}

:deep(address > .fast-footer-title) {
  margin-bottom: 24px;
}

:deep(address .q-icon) {
  font-size: 16px;
  line-height: 16px;
}

:deep(.fast-footer-social) {
  gap: 10px;
}

:deep(.fast-social-button) {
  width: 50px;
  height: 50px;
  min-width: 50px;
}

@media (max-width: 1023px) {
  .fast-ecommerce-footer {
    @apply tw-gap-8 tw-pt-6 tw-pb-20;
  }

  .fast-footer-widgets {
    row-gap: 32px;
  }

  .fast-footer-copyright {
    padding-top: 0;
  }

  .fast-footer-copyright-text {
    display: block;
    text-align: center;
  }

  :deep(.fast-footer-main > .fast-footer-widgets > :first-child) {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  :deep(.fast-footer-main > .fast-footer-widgets > :first-child > .tw-space-y-4 > :not([hidden]) ~ :not([hidden])) {
    margin-top: 0;
  }

  :deep(.fast-footer-main > .fast-footer-widgets > :first-child > .tw-mb-4) {
    margin-bottom: 0;
  }

  :deep(.fast-footer-widget) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  :deep(.fast-footer-title) {
    margin-bottom: 0;
  }

  :deep(.fast-footer-widget > h2) {
    margin-bottom: 0;
  }

  :deep(address > .fast-footer-title) {
    margin-bottom: 0;
  }

  :deep(.footer-contact-row) {
    gap: 8px;
  }
}
</style>
